'use client';

import { FieldKind, ValueShape } from '@inixiative/json-rules';
import {
  type ArrayNode,
  type GroupNode,
  getNode,
  type LeafNode,
  setNode,
  type ValueControl,
  type VariableControl,
} from '@inixiative/rules-builder';
import { formatLocalDay, parseDateOnly } from '@zealot/shared';
import { ItemIcon } from '@zealot/ui/components/combobox-select';
import { Input } from '@zealot/ui/components/input';
import { SimpleSelect } from '@zealot/ui/components/simple-select';
import { ValueInput } from '@zealot/ui/components/value-input';
import { cn } from '@zealot/ui/lib/utils';
import { X } from 'lucide-react';
import { useContext, useMemo, useRef } from 'react';
import {
  alphabetical,
  BuilderContext,
  coerceLeafValue,
  commitDeferred,
  curateOperators,
  isRangeShape,
  MutateContext,
  type RuleBuilderValue,
  relabelOperators,
  sectionedItems,
  WINDOW_CHOICES,
} from './lib';

// gloss
export const RemoveButton = ({ onClick }: { onClick?: () => void }) => (
  <button
    type="button"
    aria-label="Remove"
    onClick={onClick}
    disabled={!onClick}
    className={cn('ml-auto text-muted-foreground transition-colors hover:text-destructive', !onClick && 'invisible')}
  >
    <X className="size-4" />
  </button>
);

// gloss
export const CombinatorSelect = ({ operator }: { operator: GroupNode['operator'] }) => (
  <SimpleSelect
    aria-label="Match"
    items={[
      { value: 'all', label: 'all' },
      { value: 'any', label: 'any' },
    ]}
    value={operator.value}
    onValueChange={(next) => next && commitDeferred(() => operator.set(next as 'all' | 'any'))}
    size="sm"
    className="h-7 w-fit border-none bg-primary/10 font-semibold text-primary"
  />
);

export const MatchToggle = ({ operator }: { operator: GroupNode['operator'] }) => (
  <div
    data-slot="match-toggle"
    className="inline-flex overflow-hidden rounded-md border border-border font-medium text-xs"
  >
    {(['all', 'any'] as const).map((op) => (
      <button
        key={op}
        type="button"
        aria-pressed={operator.value === op}
        onClick={() => commitDeferred(() => operator.set(op))}
        className={cn(
          'px-2.5 py-1 transition-colors',
          operator.value === op
            ? 'bg-primary text-primary-foreground'
            : 'bg-background text-muted-foreground hover:text-foreground',
        )}
      >
        {op === 'all' ? 'ALL' : 'ANY'}
      </button>
    ))}
  </div>
);

type RangeBoundsControl = {
  current?: unknown;
  kind?: ValueControl['kind'];
  set: (value: unknown) => void;
};

// gloss
export const RangeValue = ({
  value,
  kind = value.kind,
  dashedWhenEmpty,
}: {
  value: RangeBoundsControl;
  kind?: ValueControl['kind'];
  dashedWhenEmpty?: boolean;
}) => {
  const current = Array.isArray(value.current) ? (value.current as unknown[]) : [undefined, undefined];
  const setBound = (index: 0 | 1, next: unknown) => {
    const pair: unknown[] = [current[0], current[1]];
    pair[index] = next;
    value.set(pair as never);
  };
  const isDay = kind === FieldKind.DateTime;
  return (
    <div data-slot="rule-range" className="flex items-center gap-1.5">
      {([0, 1] as const).map((index) =>
        isDay ? (
          <ValueInput
            key={index}
            spec={{ kind: FieldKind.DateTime, shape: ValueShape.dateValue, displayFormat: 'yyyy-MM-dd' }}
            value={parseDateOnly(current[index] == null ? null : String(current[index]).slice(0, 10))}
            onChange={(date) =>
              commitDeferred(() => setBound(index, date instanceof Date ? formatLocalDay(date) : undefined))
            }
            placeholder="YYYY-MM-DD"
            size="sm"
            className="w-36"
            aria-label={index === 0 ? 'From' : 'To'}
          />
        ) : (
          <ValueInput
            key={index}
            spec={{ kind: kind ?? FieldKind.String, ...(kind === FieldKind.Float ? { step: 'any' } : {}) }}
            value={current[index] == null ? '' : String(current[index])}
            onChange={(next) => setBound(index, coerceLeafValue(kind, String(next ?? '')))}
            size="sm"
            className="w-24"
            dataEmpty={Boolean(dashedWhenEmpty && current[index] == null)}
            aria-label={index === 0 ? 'From' : 'To'}
          />
        ),
      )}
    </div>
  );
};

// gloss
// why: a field-to-field comparison must be recognized and shown read-only — one keystroke on the
// why: naive editor silently rewrites it into a literal on an auto-approve rule
const comparedPathsInTree = (node: unknown, found: Map<string, Set<string>>): Map<string, Set<string>> => {
  if (Array.isArray(node)) {
    for (const child of node) comparedPathsInTree(child, found);
    return found;
  }
  if (!node || typeof node !== 'object') return found;

  const record = node as Record<string, unknown>;
  if (typeof record.path === 'string' && typeof record.field === 'string') {
    const key = `${record.field}\u0000${String(record.operator)}`;
    found.set(key, (found.get(key) ?? new Set()).add(record.path));
  }
  for (const child of Object.values(record)) comparedPathsInTree(child, found);
  return found;
};

// gloss
const AMBIGUOUS_OPERAND = '\u0000ambiguous';

const comparedFieldLabel = (raw: string, decoration?: { labels?: { fields?: Record<string, unknown> } }) => {
  if (raw === AMBIGUOUS_OPERAND) return 'another field';
  const fields = decoration?.labels?.fields ?? {};
  const entry = Object.entries(fields).find(([key]) => key === raw || key.endsWith(`.${raw}`));
  const label = (entry?.[1] as { label?: string } | undefined)?.label;
  return label ?? raw;
};

// gloss
export const OperatorValue = ({ node }: { node: LeafNode }) => {
  const { operatorLabels, decoration } = useContext(BuilderContext);
  const { value: tree } = useContext(MutateContext);
  // why: a literal-valued leaf must not be matched as a field-to-field comparison just because it
  // why: shares field+operator — doing so drops its editor and its visible value
  const literal = node.value?.current;
  const hasLiteral = Array.isArray(literal) ? literal.length > 0 : literal != null && literal !== '';
  const comparedPath = useMemo(() => {
    if (hasLiteral) return null;
    const key = `${node.field?.value}\u0000${node.operator?.value}`;
    const matches = comparedPathsInTree(tree, new Map()).get(key);
    if (!matches?.size) return null;
    return matches.size === 1 ? [...matches][0]! : AMBIGUOUS_OPERAND;
  }, [tree, node.field?.value, node.operator?.value, hasLiteral]);
  const value = node.value;
  const dateInput = value?.kind === 'DateTime';
  const isInvalid = value?.valid === false && value.current != null && value.current !== '';
  const options = value?.options?.length ? value.options : undefined;
  const multiItems = useMemo(
    () => (options && value?.shape !== 'dayList' ? alphabetical(options) : options),
    [options, value?.shape],
  );
  const pickItems = useMemo(() => (options ? sectionedItems(options) : []), [options]);
  return (
    <>
      <SimpleSelect
        aria-label="Operator"
        items={relabelOperators(
          curateOperators(node.operator!.options, value?.kind, node.operator!.value),
          operatorLabels,
        )}
        value={node.operator!.value ?? null}
        onValueChange={(next) => next && commitDeferred(() => node.operator!.set(next))}
        placeholder="operator"
        size="sm"
        className="w-44"
      />
      {}
      {comparedPath ? (
        <span
          data-slot="rule-compared-field"
          className="inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1 text-muted-foreground text-sm"
        >
          <span className="text-xs uppercase tracking-wide">compared to</span>
          <span className="text-foreground">{comparedFieldLabel(comparedPath, decoration)}</span>
        </span>
      ) : value!.shape === 'none' ? null : value!.shape === 'dateWindow' ? (
        <RelativeDateSelect node={node} />
      ) : isRangeShape(value!.shape) ? (
        <RangeValue value={node.value!} />
      ) : value!.shape === 'array' || value!.shape === 'dayList' ? (
        options ? (
          <ValueInput
            spec={{ kind: FieldKind.Enum, shape: ValueShape.array, options: multiItems! }}
            value={Array.isArray(value!.current) ? (value!.current as string[]) : []}
            onChange={(next) => commitDeferred(() => value!.set(next as string[]))}
            size="sm"
            className="min-w-44"
          />
        ) : (
          <ValueInput
            spec={{ kind: value!.kind ?? FieldKind.String, shape: ValueShape.array }}
            value={Array.isArray(value!.current) ? (value!.current as string[]) : []}
            onChange={(next) => value!.set(next as string[])}
            aria-label="Add value"
          />
        )
      ) : options ? (
        <ValueInput
          spec={{ kind: FieldKind.Enum, options: pickItems }}
          value={value!.current == null ? null : String(value!.current)}
          onChange={(next) => next != null && commitDeferred(() => value!.set(next as string))}
          placeholder="value"
          size="sm"
          className={cn('w-44', isInvalid && 'border-destructive text-destructive')}
          aria-label="Value"
        />
      ) : dateInput ? (
        <ValueInput
          spec={{ kind: FieldKind.DateTime, shape: ValueShape.dateValue, displayFormat: 'yyyy-MM-dd' }}
          value={parseDateOnly(value!.current == null ? null : String(value!.current).slice(0, 10))}
          onChange={(next) => commitDeferred(() => value!.set(next instanceof Date ? formatLocalDay(next) : undefined))}
          placeholder="YYYY-MM-DD"
          size="sm"
          className="w-44"
          aria-label="Value"
        />
      ) : value!.kind === 'Boolean' ? (
        <ValueInput
          spec={{ kind: FieldKind.Boolean, as: 'select' }}
          value={value!.current == null ? null : String(value!.current) === 'true'}
          onChange={(next) => commitDeferred(() => value!.set(next as boolean))}
          placeholder="value"
          size="sm"
          className="w-44"
          aria-label="Value"
        />
      ) : (
        <ValueInput
          spec={{ kind: value!.kind ?? FieldKind.String, ...(value!.kind === FieldKind.Float ? { step: 'any' } : {}) }}
          value={value!.current == null ? '' : String(value!.current)}
          onChange={(next) => value!.set(coerceLeafValue(value!.kind, String(next ?? '')))}
          size="sm"
          className="w-44"
          aria-label="Value"
        />
      )}
    </>
  );
};

export const PresetChip = ({ icon, label }: { icon?: string; label: string }) => (
  <span className="flex items-center gap-1.5 font-medium text-sm">
    {icon ? <ItemIcon icon={icon} label={label} /> : null}
    {label}
  </span>
);

// gloss
export const RelativeDateSelect = ({ node }: { node: LeafNode }) => {
  const value = node.value!;
  const currentJson = JSON.stringify(value.current);
  const currentKey =
    value.current == null
      ? 'allTime'
      : (WINDOW_CHOICES.find((choice) => choice.expr && JSON.stringify(choice.expr) === currentJson)?.key ?? 'custom');
  const items = [
    ...WINDOW_CHOICES.map(({ key, label }) => ({ value: key, label })),
    ...(currentKey === 'custom' ? [{ value: 'custom', label: 'custom window' }] : []),
  ];
  return (
    <SimpleSelect
      aria-label="Window"
      items={items}
      value={currentKey}
      onValueChange={(next) => {
        const choice = WINDOW_CHOICES.find((entry) => entry.key === next);
        if (!choice) return;
        commitDeferred(() => (choice.expr ? value.set(choice.expr) : node.remove()));
      }}
      placeholder="window"
      size="sm"
      className="w-44"
    />
  );
};

// gloss
export const VariableSlotControl = ({ slot }: { slot: VariableControl }) => {
  const { value } = slot;
  const current = value.current;

  if (slot.options?.length) {
    const keyOf = (raw: unknown) => (typeof raw === 'string' ? raw : JSON.stringify(raw));
    const items = slot.options.map((option) => ({ value: keyOf(option.value), label: option.label }));
    return (
      <ValueInput
        spec={{ kind: FieldKind.Enum, options: items }}
        value={current == null ? null : keyOf(current)}
        onChange={(next) => {
          const picked = slot.options?.find((option) => keyOf(option.value) === next);
          if (picked) commitDeferred(() => value.set(picked.value));
        }}
        placeholder="value"
        className="w-44"
        aria-label={slot.label ?? 'Value'}
      />
    );
  }

  if (value.shape === 'dateWindow') {
    const ago = (current as { ago?: { days?: unknown } } | undefined)?.ago;
    const currentJson = current != null ? JSON.stringify(current) : undefined;
    const currentKey =
      current == null
        ? 'open'
        : (WINDOW_CHOICES.find((choice) => choice.expr && JSON.stringify(choice.expr) === currentJson)?.key ??
          'custom');
    const items = [
      ...WINDOW_CHOICES.filter((choice) => choice.expr).map(({ key, label }) => ({ value: key, label })),
      ...(currentKey === 'custom'
        ? [{ value: 'custom', label: 'custom window' }]
        : currentKey === 'open'
          ? [{ value: 'open', label: 'pick a window' }]
          : []),
    ];
    const days = typeof ago?.days === 'number' ? String(ago.days) : '';
    return (
      <>
        <SimpleSelect
          aria-label={slot.label ?? 'Window'}
          items={items}
          value={currentKey}
          onValueChange={(next) => {
            const choice = WINDOW_CHOICES.find((entry) => entry.key === next);
            if (choice?.expr) commitDeferred(() => value.set(choice.expr));
          }}
          placeholder="window"
          size="sm"
          className="w-44"
        />
        {ago ? (
          <>
            <Input
              type="number"
              aria-label="Days"
              min={1}
              value={days}
              onChange={(event) => {
                const raw = event.target.value;
                value.set(raw === '' ? { ago: {} } : { ago: { days: Number(raw) } });
              }}
              placeholder="days"
              className={cn('h-9 w-24', days === '' && 'border-dashed')}
            />
            <span className="text-muted-foreground text-xs">days</span>
          </>
        ) : null}
      </>
    );
  }

  // why: an aggregate threshold slot carries no `mode` and no `kind` — the shape question cannot
  // why: sit behind the leaf narrowing, and the kind has to be supplied
  if (isRangeShape(value.shape)) {
    const rangeKind = 'mode' in value ? (value as ValueControl).kind : 'Int';
    return <RangeValue value={value} kind={rangeKind} dashedWhenEmpty />;
  }

  const leaf = 'mode' in value ? (value as ValueControl) : undefined;

  if (leaf?.kind === FieldKind.Boolean) {
    return (
      <ValueInput
        spec={{ kind: FieldKind.Boolean, as: 'select' }}
        value={current == null ? null : String(current) === 'true'}
        onChange={(next) => commitDeferred(() => value.set(next as boolean))}
        placeholder="value"
        size="sm"
        className="w-44"
        aria-label={slot.label ?? 'Value'}
      />
    );
  }

  const kind = leaf?.kind ?? FieldKind.Int;
  return (
    <ValueInput
      spec={{ kind, ...(kind === FieldKind.Float ? { step: 'any' } : {}) }}
      value={current == null ? '' : String(current)}
      onChange={(next) => value.set(coerceLeafValue(kind, String(next ?? '')))}
      placeholder="value"
      size="sm"
      className="w-28"
      dataEmpty={current == null}
      aria-label={slot.label ?? 'Value'}
    />
  );
};

// gloss
export const DEFAULT_DATE_FIELD = 'created_at';

// gloss
export const DateWindowControl = ({ node, leaf }: { node: ArrayNode; leaf?: LeafNode }) => {
  const { value, setCondition } = useContext(MutateContext);
  const currentJson = leaf?.value?.current != null ? JSON.stringify(leaf.value.current) : undefined;
  const currentKey =
    !leaf || leaf.value?.current == null
      ? 'allTime'
      : (WINDOW_CHOICES.find((choice) => choice.expr && JSON.stringify(choice.expr) === currentJson)?.key ?? 'custom');
  const items = [
    ...WINDOW_CHOICES.map(({ key, label }) => ({ value: key, label })),
    ...(currentKey === 'custom' ? [{ value: 'custom', label: 'custom window' }] : []),
  ];
  const fieldRef = useRef(DEFAULT_DATE_FIELD);
  if (leaf?.field?.value) fieldRef.current = String(leaf.field.value);

  const setWindow = (key: string) => {
    const choice = WINDOW_CHOICES.find((entry) => entry.key === key);
    if (!choice) return;
    if (leaf) {
      leaf.value?.set(choice.expr);
      return;
    }
    if (!choice.expr || value == null || !setCondition) return;
    const target = getNode(value, node.path) as Record<string, unknown> | undefined;
    if (!target || target.field !== node.field.value) return;
    const inner = (target.condition ?? { all: [] }) as Record<string, unknown>;
    const operatorKey: 'all' | 'any' = Array.isArray(inner.any) ? 'any' : 'all';
    const conditions = Array.isArray(inner[operatorKey]) ? (inner[operatorKey] as unknown[]) : [inner];
    const newClause = { field: fieldRef.current, dateOperator: 'within', value: choice.expr };
    const appended = {
      ...target,
      condition: { ...(Array.isArray(inner[operatorKey]) ? inner : {}), [operatorKey]: [...conditions, newClause] },
    };
    setCondition(setNode(value, node.path, appended as never) as RuleBuilderValue);
  };

  return (
    <SimpleSelect
      aria-label="Window"
      items={items}
      value={currentKey}
      onValueChange={(next) => next && commitDeferred(() => setWindow(next))}
      placeholder="window"
      size="sm"
      className="w-44"
    />
  );
};
